// client/src/components/dives/CreatureListTile.js (updated)
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  Box,
  Chip,
} from "@mui/material";
import axios from "axios";
import { useAuth } from "../../contexts/AuthContext";
import { useUnits } from "../../contexts/UnitContext";
import BugReportIcon from "@mui/icons-material/BugReport";
import CreatureDetailModal from "./CreatureDetailModal"; // Import the new modal

// Create axios instance for better performance
const api = axios.create({
  baseURL: "/api",
});

const CreatureListTile = React.memo(function CreatureListTile({ divesiteId }) {
  console.log(`[CreatureListTile] Rendering for divesiteId: ${divesiteId}`);

  const [creatureData, setCreatureData] = useState({
    allCreatures: [],
    userCreatures: [],
  });
  const [loadingState, setLoadingState] = useState({
    loading: true,
    error: null,
  });

  // State for the creature detail modal
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedCreature, setSelectedCreature] = useState(null);

  const { userObjectId, currentUser } = useAuth();
  const { convert } = useUnits();
  const theme = useTheme();

  // Updated container styles for a more defined, less diffused look and whiter background
  const containerStyles = useMemo(
    () => ({
      background: "#FFFFFF", // Explicitly white background
      borderRadius: "16px", // Slightly smaller border radius for a sharper edge
      boxShadow: theme.shadows[3], // Use a standard, less "floaty" shadow from the theme
      border: `1px solid ${theme.palette.grey[200]}`, // Lighter, more defined border
      transition: "all 0.3s ease-in-out", // Slightly faster transition
      width: "100%",

      flex: 1,
      mt: 2,
      overflow: "hidden",
      "&:hover": {
        transform: "translateY(-2px)", // Subtle lift on hover
        boxShadow: theme.shadows[6], // Slightly more pronounced shadow on hover
      },
    }),
    [theme] // Add theme as a dependency for useMemo
  );

  const headerStyles = useMemo(
    () => ({
      // Retained diffused purple tone, but increased alpha for more prominence
      background:
        "linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)", // Increased alpha
      borderBottom: `1px solid ${theme.palette.grey[200]}`, // Lighter, more defined bottom border
      p: 3,
    }),
    [theme]
  );

  // Use a single fetch function for both data sets
  const fetchData = useCallback(async () => {
    if (!divesiteId || !userObjectId) {
      console.warn(
        "[CreatureListTile] Skipping fetch: missing divesiteId or userObjectId"
      );
      setLoadingState({ loading: false, error: null });
      return;
    }

    console.log(
      `[CreatureListTile] Starting data fetch for divesiteId: ${divesiteId}, userObjectId: ${userObjectId}`
    );
    setLoadingState({ loading: true, error: null });

    try {
      const token = await currentUser.getIdToken();
      console.log("[CreatureListTile] Firebase ID token obtained.");

      const headers = {
        Authorization: `Bearer ${token}`,
      };

      const [allCreaturesResponse, userCreaturesResponse] = await Promise.all([
        api.get(`/creatures/divesites/${divesiteId}/creatures`, { headers }),
        api.get(`/diver/${userObjectId}/divesites/${divesiteId}/creatures`, {
          headers,
        }),
      ]);

      console.log("[CreatureListTile] Data fetched successfully.");
      console.log("All Creatures Response:", allCreaturesResponse.data);
      console.log("User Creatures Response:", userCreaturesResponse.data);

      setCreatureData({
        allCreatures: allCreaturesResponse.data,
        userCreatures: userCreaturesResponse.data,
      });

      setLoadingState({ loading: false, error: null });
    } catch (err) {
      console.error("[CreatureListTile] Error fetching creatures:", err);
      setLoadingState({ loading: false, error: err });
    }
  }, [divesiteId, userObjectId, currentUser]);

  useEffect(() => {
    console.log(
      `[CreatureListTile] useEffect triggered for divesiteId: ${divesiteId}, userObjectId: ${userObjectId}`
    );
    fetchData();
  }, [fetchData]);

  // Handlers for the Creature Detail Modal
  const handleOpenDetailModal = useCallback((creature) => {
    setSelectedCreature(creature);
    setIsDetailModalOpen(true);
  }, []);

  const handleCloseDetailModal = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedCreature(null); // Clear selected creature when closing
  }, []);

  const { loading, error } = loadingState;
  const { allCreatures, userCreatures } = creatureData;

  if (loading) console.log("[CreatureListTile] Rendering loading state...");
  if (error)
    console.error("[CreatureListTile] Rendering error state:", error.message);
  if (!loading && !error)
    console.log(
      `[CreatureListTile] Rendering data state. User creatures: ${userCreatures.length}, All creatures: ${allCreatures.length}`
    );

  if (loading) {
    return (
      <Paper
        sx={{
          ...containerStyles,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={containerStyles}>
        <Alert
          severity="error"
          sx={{
            borderRadius: "16px",
            background: theme.palette.error.light + "1A", // Using alpha for transparency
            border: `1px solid ${theme.palette.error.main}`,
            color: theme.palette.error.main,
          }}
        >
          {error.message}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={containerStyles}>
      {/* Header Section */}
      <Box sx={headerStyles}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "12px",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
            }}
          >
            <BugReportIcon sx={{ color: "white", fontSize: "20px" }} />
          </Box>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Marine Life Spotted
          </Typography>
        </Stack>
      </Box>

      {/* Content Section */}
      <Box sx={{ p: 3 }}>
        {allCreatures.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              background: theme.palette.grey[50], // Lighter background for no content
              borderRadius: "12px", // Consistent border radius
              border: `1px dashed ${theme.palette.grey[300]}`, // Lighter dashed border for "no content"
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontStyle: "italic",
                color: theme.palette.text.secondary,
                fontSize: "16px",
              }}
            >
              No Marine Life Logged Yet
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.secondary,
                mt: 1,
                fontSize: "14px",
              }}
            >
              Be the first to log a creature sighting!
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {allCreatures.map((item, index) => {
              if (!item.creature) return null;
              const key = item.creature?._id || `all-creature-${index}`;

              // Determine if the user has spotted this creature
              const isUserSpotted = userCreatures.some(
                (userItem) => userItem.creature?._id === item.creature?._id
              );

              // Get dates from either timestamp or recentSightingDates array
              let dates = [];
              if (item.timestamp) {
                dates.push(item.timestamp);
              } else if (
                item.recentSightingDates &&
                item.recentSightingDates.length > 0
              ) {
                dates = [...item.recentSightingDates]
                  .sort((a, b) => new Date(b) - new Date(a))
                  .slice(0, 3); // Show only 3 most recent dates
              }

              const mostRecentDate = dates.length > 0 ? dates[0] : null;

              return (
                <Chip
                  key={key}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        py: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        // Inherit color from Chip's color prop, which is now lighter
                        sx={{ fontSize: "13px" }}
                      >
                        {item.creature?.commonName || "Unknown Species"}
                      </Typography>
                      {mostRecentDate && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "11px",
                            opacity: 0.8,
                            mt: 0.25,
                            color: theme.palette.text.secondary, // Lighter grey for dates
                          }}
                        >
                          {convert.time.formatDate(new Date(mostRecentDate))}
                        </Typography>
                      )}
                      {dates.length > 1 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "10px",
                            opacity: 0.6,
                            fontStyle: "italic",
                            color: theme.palette.text.secondary, // Lighter grey for "+X more"
                          }}
                        >
                          +{dates.length - 1} more
                        </Typography>
                      )}
                    </Box>
                  }
                  onClick={() => handleOpenDetailModal(item.creature)} // Add onClick here
                  sx={{
                    background: isUserSpotted
                      ? `linear-gradient(135deg, ${theme.palette.success.light}30 0%, ${theme.palette.success.main}20 100%)` // Increased alpha for user's creatures
                      : `linear-gradient(135deg, ${theme.palette.primary.light}20 0%, ${theme.palette.secondary.light}10 100%)`, // Increased alpha for others
                    border: isUserSpotted
                      ? `1px solid ${theme.palette.success.main}50`
                      : `1px solid ${theme.palette.grey[300]}`, // Lighter border for other chips
                    color: isUserSpotted
                      ? theme.palette.success.dark // Keep dark for strong contrast on success chips
                      : theme.palette.grey[800], // Lighter grey for other chips text
                    borderRadius: "12px",
                    height: "auto",
                    minHeight: "48px",
                    px: 2,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.03) translateY(-1px)", // Less aggressive hover
                      boxShadow: theme.shadows[2], // Lighter shadow on hover
                      background: isUserSpotted
                        ? `linear-gradient(135deg, ${theme.palette.success.light}40 0%, ${theme.palette.success.main}30 100%)`
                        : `linear-gradient(135deg, ${theme.palette.primary.light}30 0%, ${theme.palette.secondary.light}20 100%)`,
                    },
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* Stats Footer */}
        {allCreatures.length > 0 && (
          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: `1px solid ${theme.palette.grey[200]}`,
            }} // Lighter border for footer
          >
            <Typography
              variant="caption"
              color={theme.palette.grey[700]} // Lighter grey for footer text
              sx={{
                textAlign: "center",
                display: "block",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {allCreatures.length} unique species spotted at this dive site
              {userObjectId && ` (${userCreatures.length} by you)`}{" "}
              {/* Added user count */}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Creature Detail Modal */}
      <CreatureDetailModal
        open={isDetailModalOpen}
        onClose={handleCloseDetailModal}
        creature={selectedCreature}
      />
    </Paper>
  );
});

export default React.memo(CreatureListTile);
